import Vue from 'vue';
import moment from '@/utils/moment-wrapper.ts';
import values from 'lodash/values';
import keyBy from 'lodash/keyBy';
import pick from 'lodash/pick';
import { PerSubscriptionUserStorage } from '@/utils/storage';
import {
    fetchDashboards,
    fetchDashboard,
    addSharedDashboard,
    deleteDashboard,
    createDashboard,
    updateDashboard,
    fetchDashboardPermissions,
    shareDashboardWithUsers,
    updateDashboardSubPermissions,
    updateDashboardUserPermissions,
    unshareUserFromDashboard,
} from './dashboards.utils';
import { v4 as uuid } from 'uuid';
import widgetDomSettings from '@/dashboards-v2/widget-maps/widget-dom-settings.map';

let loadAllPromise;

const dashboards = {
    namespaced: true,
    state: {
        error: null,
        portfolioSummary: null,
        fetching: false,
        fetchingByQuery: false,
        fetchingPermissions: false,
        fetched: false,
        fetchedByQuery: false,
        map: {},
        dashboardFilterMap: undefined,
        creating: false,
        pesDrilldown: {
            account: null,
            appId: 0,
            opened: false,
            score: 0,
            segmentId: 0,
            timeSeries: null,
            type: 0,
            widgetId: null,
        },
    },
    mutations: {
        setMapAtKey (state, { dashboard }) {
            Vue.set(state.map, dashboard.id, dashboard);
        },
        updateMap (state, { values }) {
            const dashMap = keyBy(values, 'id');
            const mergedDashMap = { ...state.map, ...dashMap };
            state.map = mergedDashMap;
        },
        deleteMapAtKey (state, { id }) {
            Vue.delete(state.map, id);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setFetchingByQuery (state, { fetching }) {
            state.fetchingByQuery = fetching;
        },
        setFetchingPermissions (state, { fetching }) {
            state.fetchingPermissions = fetching;
        },
        setFetched (state, { fetched }) {
            state.fetched = fetched;
        },
        setFetchedByQuery (state, { fetched }) {
            state.fetchedByQuery = fetched;
        },
        setPortfolioSummary (state, { dashboard }) {
            state.portfolioSummary = dashboard;
        },
        setDashboardProperty (state, { id, propertyKey, propertyValue }) {
            Vue.set(state.map[id], propertyKey, propertyValue);
        },
        updateDashboardProperties (state, { dashboard }) {
            Object.keys(dashboard).forEach((key) => {
                Vue.set(state.map[dashboard.id], key, dashboard[key]);
            });
        },
        setPesDrilldown (state, options) {
            for (const optionKey in options) {
                if (state.pesDrilldown[optionKey] !== undefined) {
                    Vue.set(state.pesDrilldown, optionKey, options[optionKey]);
                }
            }
        },
        setDashboardModules (state, { id, modules }) {
            Vue.set(state.map[id].options, 'modules', modules);
        },
        setDashboardWidget (state, { dashboardId, widgetId, widget }) {
            const { widgets } = state.map[dashboardId];
            const index = widgets.findIndex((currWidget) => currWidget.id === widgetId);
            widgets.splice(index, 1, widget);
        },
        setDashboardFilterMap (state, dashboardFilterMap) {
            state.dashboardFilterMap = dashboardFilterMap;
        },
        setDashboardFilterMapAtKey (state, { dashboardId, filterSettings }) {
            state.dashboardFilterMap = {
                ...state.dashboardFilterMap,
                [dashboardId]: filterSettings,
            };
        },
        addDashboardWidgets (state, { id, newWidgets, position = 0 }) {
            const { widgets } = state.map[id];
            newWidgets.forEach((widget) => {
                if (widget.isDuplicate) {
                    widgets.splice(position + 1, 0, widget);
                } else {
                    widgets.unshift(widget);
                }
            });
        },
        removeDashboardWidget (state, { dashboardId, widgetId }) {
            const { widgets } = state.map[dashboardId];
            const index = widgets.findIndex((currWidget) => currWidget.id === widgetId);
            widgets.splice(index, 1);
        },
        setCreating (state, { creating }) {
            state.creating = creating;
        },
    },
    actions: {
        /**
         * Fetch any dashboards either created by or explicitly shared with the current user.
         * To retrieve dashboards shared with an entire subscription, use loadByQuery.
         * This loadAll action does not fetch sub-shared dashboards to maintain backwards-compatibility.
         */
        async loadAll ({ commit }, { reFetch = false } = {}) {
            if (loadAllPromise && !reFetch) {
                return loadAllPromise;
            }

            commit('setFetching', { fetching: true });
            try {
                loadAllPromise = fetchDashboards({
                    include: 'createdByMe,sharedWithMe',
                });
                const dashboards = await loadAllPromise;

                const portfolioSummaryIndex = dashboards.findIndex(({ options }) => options && options.isExecutive);

                if (portfolioSummaryIndex > -1) {
                    const portfolioSummary = dashboards.splice(portfolioSummaryIndex, 1)[0];
                    commit('setPortfolioSummary', { dashboard: portfolioSummary });
                }

                commit('setFetching', { fetching: false });
                commit('updateMap', {
                    values: dashboards,
                });
                commit('setFetched', { fetched: true });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                loadAllPromise = null;
                throw error;
            }
        },
        async loadByQuery ({ commit }, { query = 'all' } = {}) {
            commit('setFetchingByQuery', { fetching: true });
            try {
                const dashboards = await fetchDashboards({ include: query });

                const portfolioSummaryIndex = dashboards.findIndex(({ options }) => options && options.isExecutive);

                if (portfolioSummaryIndex > -1) {
                    const portfolioSummary = dashboards.splice(portfolioSummaryIndex, 1)[0];
                    commit('setPortfolioSummary', { dashboard: portfolioSummary });
                }

                commit('setFetchingByQuery', { fetching: false });
                await loadAllPromise;
                commit('updateMap', {
                    values: dashboards,
                });
                commit('setFetchedByQuery', { fetched: true });
            } catch (error) {
                commit('setFetchingByQuery', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async loadOne ({ commit, state }, { reFetch = false, id } = {}) {
            if (state.map[id] && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });
            try {
                const dashboard = await fetchDashboard(id);
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', { dashboard });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async loadDashboardFilterMap ({ state, commit }) {
            if (state.dashboardFilterMap) return;
            const dashboardFilterMap = (await PerSubscriptionUserStorage.get('dashboardFilterMap')) || {};

            commit('setDashboardFilterMap', dashboardFilterMap?.value || {});
        },
        async addDashboard ({ commit }, dashboard) {
            await loadAllPromise;
            commit('setMapAtKey', { dashboard });
        },
        async createV2Dashboard ({ state, commit }, dashboard) {
            if (state.creating) return;

            commit('setCreating', { creating: true });
            const newDashboard = await createDashboard(dashboard);
            newDashboard.isNew = true;
            await loadAllPromise;
            commit('setMapAtKey', { dashboard: newDashboard });
            commit('setCreating', { creating: false });

            return newDashboard;
        },
        async createPortfolioSummary ({ commit }, dashboard) {
            const newDashboard = await createDashboard(dashboard);
            commit('setPortfolioSummary', { dashboard: newDashboard });
        },
        async deleteDashboard ({ commit }, id) {
            try {
                await deleteDashboard(id);
                await loadAllPromise;
                commit('deleteMapAtKey', { id });
            } catch (error) {
                throw error;
            }
        },
        async fetchDashboardPermissions ({ commit, state }, { reFetch = false, id }) {
            if (state.map[id]?.subscriptionSharing && !reFetch) {
                return;
            }

            commit('setFetchingPermissions', { fetching: true });
            try {
                const permissionInfo = await fetchDashboardPermissions(id);
                await loadAllPromise;
                commit('setDashboardProperty', {
                    id,
                    propertyKey: 'subscriptionSharing',
                    propertyValue: permissionInfo.subscriptionSharing,
                });
                if (permissionInfo.userSharing) {
                    commit('setDashboardProperty', {
                        id,
                        propertyKey: 'userSharing',
                        propertyValue: permissionInfo.userSharing,
                    });
                }
                commit('setFetchingPermissions', { fetching: false });
            } catch (error) {
                commit('setFetchingPermissions', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async shareDashboardWithUsers ({ commit }, { id, params }) {
            const permissionInfo = await shareDashboardWithUsers(id, params);
            await loadAllPromise;
            commit('updateDashboardProperties', {
                dashboard: { id, isShared: permissionInfo.isShared, userSharing: permissionInfo.userSharing },
            });
        },
        async updateDashboardUserPermissions ({ commit }, { dashboardId, userId, permission }) {
            const permissionInfo = await updateDashboardUserPermissions(dashboardId, userId, permission);
            await loadAllPromise;
            commit('updateDashboardProperties', {
                dashboard: {
                    id: dashboardId,
                    userSharing: permissionInfo.userSharing,
                    userDetails: permissionInfo.userDetails,
                },
            });
        },
        async updateDashboardSubPermissions ({ commit }, { id, permission }) {
            const permissionInfo = await updateDashboardSubPermissions(id, permission);
            await loadAllPromise;
            commit('updateDashboardProperties', {
                dashboard: {
                    id,
                    isShared: permissionInfo.isShared,
                    subscriptionSharing: permissionInfo.subscriptionSharing,
                    userDetails: permissionInfo.userDetails,
                },
            });
        },
        async updateDashboardFilters ({ state, commit }, { dashboardId, filterSettings }) {
            commit('setDashboardFilterMapAtKey', { dashboardId, filterSettings });
            await PerSubscriptionUserStorage.set('dashboardFilterMap', state.dashboardFilterMap);
        },
        async unshareUserFromDashboard ({ commit }, { dashboardId, userId }) {
            const response = await unshareUserFromDashboard(dashboardId, userId);
            await loadAllPromise;
            commit('setMapAtKey', { dashboard: response.data });
        },
        async addShared ({ commit }, id) {
            const dashboard = await addSharedDashboard(id);
            await loadAllPromise;
            commit('setMapAtKey', { dashboard });
        },
        async removeShared ({ dispatch }, id) {
            try {
                await deleteDashboard(id);
                await loadAllPromise;
                await dispatch('loadOne', { id, reFetch: true });
            } catch (error) {
                throw error;
            }
        },
        async updateCurrentDashboard ({ state, commit, dispatch }, { id, property, name, saveDashboard = true }) {
            await loadAllPromise;
            commit('setDashboardProperty', { id, propertyKey: property, propertyValue: name });
            if (saveDashboard) {
                dispatch('saveDashboard', state.map[id]);
            }
        },
        async updateCurrentDashboardModules ({ state, commit, dispatch }, { id, modules = [] } = {}) {
            await loadAllPromise;
            commit('setDashboardModules', { id, modules });
            await dispatch('saveDashboard', state.map[id]);
        },
        async addWidgetsToDashboard ({ state, commit, dispatch }, { id, widgets, skipRefetch = false, position = 0 }) {
            if (!skipRefetch) {
                await dispatch('loadOne', { id, reFetch: true });
            }
            // Deleting this section introduced APP-62268
            // Temporary, should be removed on migration (APP-54486)
            if (state.map[id]?.options) {
                delete state.map[id].options;
            }

            const completeWidgets = [];
            widgets.forEach((widget) => {
                const domSettings = widgetDomSettings[widget.type];
                const { height, width } = domSettings || {};
                const completeWidget = {
                    id: uuid(),
                    ...(height && { height }),
                    ...(width && { width }),
                    ...widget,
                    definition: {
                        layout: {
                            column: 1,
                        },
                        ...widget.definition,
                    },
                };
                completeWidgets.push(completeWidget);
            });
            await loadAllPromise;
            commit('addDashboardWidgets', { id, newWidgets: completeWidgets, position });
            await dispatch('saveDashboard', state.map[id]);
        },
        async updateDashboardHistory ({ commit, dispatch, getters }, { id }) {
            await dispatch('loadDashboardHistory');
            const dashboardHistory = getters.dashboardHistory.filter((item) => item.id !== id);

            dashboardHistory.unshift({
                id,
                timestamp: moment().valueOf(),
            });

            await PerSubscriptionUserStorage.set('dashboardHistory', dashboardHistory);
            commit('setDashboardHistory', dashboardHistory);
        },
        async updateDashboardWidget ({ state, commit, dispatch }, { dashboardId, widgetId, widget }) {
            commit('setDashboardWidget', { dashboardId, widgetId, widget });
            await dispatch('saveDashboard', state.map[dashboardId]);
        },
        async deleteWidget ({ state, commit, dispatch }, { id, deletedWidget }) {
            commit('removeDashboardWidget', { dashboardId: id, widgetId: deletedWidget.id });
            await dispatch('saveDashboard', state.map[id]);
        },
        async saveDashboard (_vuex, dashboard) {
            delete dashboard.isNew;

            return updateDashboard(dashboard);
        },
        async savePortfolioSummary ({ commit }, dashboard) {
            const savedDashboard = await updateDashboard(dashboard);
            await loadAllPromise;
            commit('setPortfolioSummary', { dashboard: savedDashboard });
        },
        closePesDrilldown ({ commit, state }) {
            if (state.pesDrilldown.opened) {
                commit('setPesDrilldown', {
                    opened: false,
                    widgetId: null,
                });
            }
        },
        openPesDrilldown ({ commit }, options) {
            commit('setPesDrilldown', {
                ...options,
                opened: true,
            });
        },
    },
    getters: {
        dashboardById: (state) => (id) => state.map[id],
        list: (state) => values(state.map),
        miniList: (state) => {
            return values(state.map).map((dash) => {
                return Object.freeze(
                    pick(dash, [
                        'id',
                        'rootVersionId',
                        'stableVersionId',
                        'name',
                        'description',
                        'kind',
                        'owner',
                        'lastUpdatedByUser',
                        'lastUpdatedAt',
                        'createdAt',
                        'createdByUser',
                        'userDetails',
                        'userSharing',
                        'subscriptionSharing',
                        'isShared',
                    ]),
                );
            });
        },
        ownerDashboards: (state, getters) =>
            getters.miniList.filter((dashboard) => dashboard.userDetails?.permission === 'owner'),
        sharedDashboards: (state, getters) =>
            getters.miniList.filter(
                (dashboard) => dashboard.userDetails?.permission !== 'owner' && dashboard.userDetails?.sharedWithMe,
            ),
        /** Returns the oldest owned dashboard, as determined by creation time */
        firstDashboard: (state, getters) => {
            return getters.ownerDashboards.length
                ? getters.ownerDashboards.reduce((a, b) => {
                    return a.createdAt < b.createdAt ? a : b;
                })
                : null;
        },
        filtersByDashboardId: (state) => (dashboardId) => {
            return (
                state.dashboardFilterMap?.[dashboardId] || {
                    selections: {},
                    activeFilterOrder: [],
                }
            );
        },
        editableDashboards: (_state, getters) =>
            getters.list.filter(
                (dashboard) =>
                    dashboard.userDetails.permission === 'owner' || dashboard.userDetails.permission === 'edit',
            ),
        pesDrilldownFilters: (state) => ({
            account: state.pesDrilldown.account,
            appId: state.pesDrilldown.appId,
            segmentId: state.pesDrilldown.segmentId,
            timeSeries: state.pesDrilldown.timeSeries,
        }),
        pesDrilldownKind: (state) => ({ type: state.pesDrilldown.type, score: state.pesDrilldown.score }),
        pesDrilldownOpened: (state) => state.pesDrilldown.opened,
        pesDrilldownWidgetId: (state) => state.pesDrilldown.widgetId,
        hasPortfolioSummaryDashboard: (state) => !!state.portfolioSummary,
    },
};

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

export default dashboards;
